import {
  getSettings,
  getLogo,
  getDarkLogo,
  getLoginImage,
  getHeroSliders,
  updateSettings,
  uploadLogo,
  uploadDarkLogo,
  uploadLoginDialogImage,
  uploadHeroSliders,
  deleteHeroSlider,
} from "../../helpers/apis";
import {
  SETTINGS_REQUEST,
  SETTINGS_SUCCESS,
  SETTINGS_ERROR,
  UPLOAD_LOGO_REQUEST,
  UPLOAD_LOGO_SUCCESS,
  UPLOAD_LOGO_ERROR,
  UPLOAD_LOGIN_DIALOG_IMAGE_REQUEST,
  UPLOAD_LOGIN_DIALOG_IMAGE_SUCCESS,
  UPLOAD_LOGIN_DIALOG_IMAGE_ERROR,
  UPLOAD_HERO_SLIDERS_REQUEST,
  UPLOAD_HERO_SLIDERS_SUCCESS,
  UPLOAD_HERO_SLIDERS_ERROR,
  DELETE_HERO_SLIDER_REQUEST,
  DELETE_HERO_SLIDER_SUCCESS,
  DELETE_HERO_SLIDER_ERROR,
  UPDATE_SETTINGS_REQUEST,
  UPDATE_SETTINGS_SUCCESS,
  UPDATE_SETTINGS_ERROR,
} from "./actionTypes";
import { showToast } from "../toast/reducer";

export const fetchSettings = () => async (dispatch) => {
  try {
    dispatch({ type: SETTINGS_REQUEST });

    // Fetch all settings from separate endpoints
    const [logoResponse, darkLogoResponse, loginImageResponse, heroSlidersResponse] = await Promise.all([
      getLogo().catch(() => ({ error: false, data: null })),
      getDarkLogo().catch(() => ({ error: false, data: null })), // Gracefully handle if dark logo endpoint doesn't exist
      getLoginImage().catch(() => ({ error: false, data: null })),
      getHeroSliders().catch(() => ({ error: false, data: [] })),
    ]);

    // Combine the responses into a single settings object
    // New API structure: logo/darkLogo returns data directly or in .data/.logo/.darkLogo
    const combinedSettings = {
      logo: logoResponse?.data?.logo || logoResponse?.data?.darkLogo || logoResponse?.data || logoResponse?.logo || logoResponse?.darkLogo || logoResponse || null,
      darkLogo: darkLogoResponse?.data?.darkLogo || darkLogoResponse?.data?.logo || darkLogoResponse?.data || darkLogoResponse?.darkLogo || darkLogoResponse?.logo || darkLogoResponse || null,
      image: loginImageResponse?.data?.image || loginImageResponse?.data || loginImageResponse?.image || loginImageResponse || null,
      sliders: heroSlidersResponse?.data?.sliders || heroSlidersResponse?.data?.uploadedFiles || (Array.isArray(heroSlidersResponse) ? heroSlidersResponse : []),
      total: heroSlidersResponse?.data?.total || heroSlidersResponse?.data?.totalUploaded || (Array.isArray(heroSlidersResponse) ? heroSlidersResponse.length : 0),
    };

    dispatch({
      type: SETTINGS_SUCCESS,
      payload: combinedSettings,
    });
  } catch (error) {
    const errorMsg = error?.response?.data?.msg || error.message || "Failed to fetch settings";
    dispatch(showToast({ type: "error", msg: errorMsg }));
    dispatch({
      type: SETTINGS_ERROR,
      payload: errorMsg,
    });
  }
};

export const uploadLogoImage = (file) => async (dispatch) => {
  try {
    dispatch({ type: UPLOAD_LOGO_REQUEST });

    const formData = new FormData();
    formData.append("logo", file);

    const response = await uploadLogo(formData);

    if (!response.error) {
      dispatch(showToast({ type: "success", msg: response.msg || "Logo uploaded successfully" }));
      dispatch({ type: UPLOAD_LOGO_SUCCESS });
      // Refresh settings after upload
      dispatch(fetchSettings());
    } else {
      dispatch(showToast({ type: "error", msg: response.msg || "Failed to upload logo" }));
      dispatch({
        type: UPLOAD_LOGO_ERROR,
        payload: response.msg || "Failed to upload logo",
      });
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.msg || error.message || "Failed to upload logo";
    dispatch(showToast({ type: "error", msg: errorMsg }));
    dispatch({
      type: UPLOAD_LOGO_ERROR,
      payload: errorMsg,
    });
  }
};

export const uploadDarkLogoImage = (file) => async (dispatch) => {
  try {
    dispatch({ type: UPLOAD_LOGO_REQUEST });

    const formData = new FormData();
    formData.append("logo", file);

    const response = await uploadDarkLogo(formData);

    if (!response.error) {
      dispatch(showToast({ type: "success", msg: response.msg || "Dark logo uploaded successfully" }));
      dispatch({ type: UPLOAD_LOGO_SUCCESS });
      // Refresh settings after upload
      dispatch(fetchSettings());
    } else {
      dispatch(showToast({ type: "error", msg: response.msg || "Failed to upload dark logo" }));
      dispatch({
        type: UPLOAD_LOGO_ERROR,
        payload: response.msg || "Failed to upload dark logo",
      });
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.msg || error.message || "Failed to upload dark logo";
    dispatch(showToast({ type: "error", msg: errorMsg }));
    dispatch({
      type: UPLOAD_LOGO_ERROR,
      payload: errorMsg,
    });
  }
};

export const uploadLoginDialogImageFile = (file) => async (dispatch) => {
  try {
    dispatch({ type: UPLOAD_LOGIN_DIALOG_IMAGE_REQUEST });

    const formData = new FormData();
    formData.append("image", file);

    const response = await uploadLoginDialogImage(formData);

    if (!response.error) {
      dispatch(showToast({ type: "success", msg: response.msg || "Login dialog image uploaded successfully" }));
      dispatch({ type: UPLOAD_LOGIN_DIALOG_IMAGE_SUCCESS });
      // Refresh settings after upload
      dispatch(fetchSettings());
    } else {
      dispatch(showToast({ type: "error", msg: response.msg || "Failed to upload login dialog image" }));
      dispatch({
        type: UPLOAD_LOGIN_DIALOG_IMAGE_ERROR,
        payload: response.msg || "Failed to upload login dialog image",
      });
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.msg || error.message || "Failed to upload login dialog image";
    dispatch(showToast({ type: "error", msg: errorMsg }));
    dispatch({
      type: UPLOAD_LOGIN_DIALOG_IMAGE_ERROR,
      payload: errorMsg,
    });
  }
};

export const uploadHeroSliderImages = (files) => async (dispatch) => {
  try {
    dispatch({ type: UPLOAD_HERO_SLIDERS_REQUEST });

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await uploadHeroSliders(formData);

    if (!response.error) {
      dispatch(showToast({ type: "success", msg: response.msg || "Hero sliders uploaded successfully" }));
      dispatch({ type: UPLOAD_HERO_SLIDERS_SUCCESS });
      // Refresh settings after upload
      dispatch(fetchSettings());
    } else {
      dispatch(showToast({ type: "error", msg: response.msg || "Failed to upload hero sliders" }));
      dispatch({
        type: UPLOAD_HERO_SLIDERS_ERROR,
        payload: response.msg || "Failed to upload hero sliders",
      });
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.msg || error.message || "Failed to upload hero sliders";
    dispatch(showToast({ type: "error", msg: errorMsg }));
    dispatch({
      type: UPLOAD_HERO_SLIDERS_ERROR,
      payload: errorMsg,
    });
  }
};

export const removeHeroSlider = (imageName) => async (dispatch) => {
  try {
    dispatch({ type: DELETE_HERO_SLIDER_REQUEST });

    const response = await deleteHeroSlider(imageName);

    if (!response.error) {
      dispatch(showToast({ type: "success", msg: response.msg || "Hero slider deleted successfully" }));
      dispatch({ type: DELETE_HERO_SLIDER_SUCCESS });
      // Refresh settings after deletion
      dispatch(fetchSettings());
    } else {
      dispatch(showToast({ type: "error", msg: response.msg || "Failed to delete hero slider" }));
      dispatch({
        type: DELETE_HERO_SLIDER_ERROR,
        payload: response.msg || "Failed to delete hero slider",
      });
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.msg || error.message || "Failed to delete hero slider";
    dispatch(showToast({ type: "error", msg: errorMsg }));
    dispatch({
      type: DELETE_HERO_SLIDER_ERROR,
      payload: errorMsg,
    });
  }
};

export const updateSettingsData = (data) => async (dispatch) => {
  try {
    dispatch({ type: UPDATE_SETTINGS_REQUEST });

    const response = await updateSettings(data);

    if (!response.error) {
      dispatch(showToast({ type: "success", msg: response.msg || "Settings updated successfully" }));
      dispatch({
        type: UPDATE_SETTINGS_SUCCESS,
        payload: response.data,
      });
    } else {
      dispatch(showToast({ type: "error", msg: response.msg || "Failed to update settings" }));
      dispatch({
        type: UPDATE_SETTINGS_ERROR,
        payload: response.msg || "Failed to update settings",
      });
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.msg || error.message || "Failed to update settings";
    dispatch(showToast({ type: "error", msg: errorMsg }));
    dispatch({
      type: UPDATE_SETTINGS_ERROR,
      payload: errorMsg,
    });
  }
};


